import Logo from "../components/Logo/Logo.js";
import Nav from "../components/Nav/Nav.js";
import csblLogo from "../images/CSBL_Logo.png";
import styled from "styled-components";
import image1 from "../images/paper_fig/Figure_1.png";
import image2 from "../images/paper_fig/Figure_6.png";
import { Link } from 'react-router-dom';
import { useState } from 'react';

// IMPORTAR O ARQUIVO JSON (Certifique-se de ajustar o caminho conforme onde você salvou)
import studiesData from '../data/v2/studiesData.json';

// COMPONENTES AUXILIARES
function AtlasSummaryContent() {
  return (
    <>
      <p>
        This website serves as an interactive platform accompanying the study <strong>“Multi-Organ Molecular Landscape of Severe COVID-19 Revealed by Consensus Gene Signatures and RAB8B Targeting.”</strong> It enables detailed examination of consensus gene signatures, gene–tissue network structures, and pathway-level perturbations derived from integrated bulk RNA-seq analyses across 11 human tissues.
      </p>
      <p style={{ marginTop: '50px' }}><strong>What you will find in this Atlas:</strong></p>
      
      <ul>
        <li>
          <Link to="/atlas"><strong>Consensus DEGs (Atlas)</strong></Link>
          <p>The integration of bulk RNA-seq datasets identified 9,449 consensus differentially expressed genes (DEGs), with peripheral blood exhibiting the highest degree of transcriptional alteration. The atlas module allows users to explore these consensus and tissue-specific gene expression patterns.</p>
        </li>
        <li style={{ marginTop: '30px' }}>
          <Link to="/coexp"><strong>Interactive Network Visualization (NetViz)</strong></Link>
          <p>Visualize the connectivity between genes, pathways, and tissues. This tool offers a global view of how molecular drivers coordinate the disease response across multiple organs.</p>
        </li>
      </ul>
    </>
  );
}

// COMPONENTE DO ACORDEÃO (Recebe data como prop, ou usa o importado)
function StudiesAccordion({ data }) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="accordion-wrapper">
            <button className="accordion-header" onClick={() => setIsOpen(!isOpen)}>
                {/* Conta dinamicamente o número de estudos */}
                <span>📚 Data Source: List of {data.length + 1} Integrated Studies</span>
                <span className={`arrow ${isOpen ? 'open' : ''}`}>▼</span>
            </button>
            
            {isOpen && (
                <div className="accordion-content">
                    <table className="studies-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Study Title</th>
                                <th>Link</th>
                                <th>Data Deposited</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((study) => (
                                <tr key={study.id}>
                                    <td>{study.id}</td>
                                    <td>{study.title}</td>
                                    <td>
                                        <a href={study.link} target="_blank" rel="noreferrer">View Paper</a>
                                    </td>
                                    <td>{study.data || "-"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// PAGE COMPONENT
export default function HomePage() {
  const mainTitle = "A Systems Biology Atlas of Severe COVID-19";

  return (
    <>
      <Nav/>
      <StyledContainer>
        
        {/* HEADER */}
        <header className="main-header-row">
            <Logo src={csblLogo} className={"logo"} />
            <h1 className="main-title-text">{mainTitle}</h1>
        </header>

        {/* SEÇÃO 1: Texto Descritivo */}
        <section className="content-box summary-section">
            <AtlasSummaryContent />
        </section>

        {/* SEÇÃO 2: Figura 1 */}
        <section className="image-box center-container">
            <h3 className="figure-title">Transcriptome Data and Consensus Gene Signatures</h3>
            <img src={image1} alt="Transcriptome Data" className="section-image" />
            <p className="figure-caption">
                <strong>A.</strong> Schematic representation of the human body illustrates the distribution of samples across diverse tissues and data types, with each tissue annotated by the corresponding sample count. <strong>B.</strong> Donut plot and bar chart portray the cell count for single-cell RNA sequencing (scRNAseq) data of peripheral blood mononuclear cells (PBMC) and other tissues in both control and COVID-19, respectively. <strong>C.</strong> Heatmap represents the Fisher test enrichment scores (-log10padj). Rows and columns represent lists of consensuses differentially expressed genes in bulk-RNAseq, demonstrating tissue-specific clustering. The analysis elucidates the robustness of the consensus gene signatures.
            </p>              
        </section>

        {/* PAINEL DE DESTAQUE */}
        <section className="content-box transition-text">
            <p>
                This interactive Atlas enables researchers to explore the data in greater depth and uncover new insights. For example, our analysis identified <strong>RAB8B</strong> as a central hub gene, a finding that not only illustrates the power of the platform but also raises a compelling biological hypothesis. This hypothesis provides a framework for future experimental validation and offers insights into potential therapeutic targets.
            </p>
        </section>

        {/* SEÇÃO 3: Figura 2 */}
        <section className="image-box center-container">
            <h3 className="figure-title">Differential expression and molecular mechanisms of RAB8B associated with severe COVID-19</h3>
            <img src={image2} alt="Key Genes Discovery" className="section-image reduced-width" /> 
            <p className="figure-caption">
                <strong>A.</strong> Schematic representation and bar chart illustrating the median log2 fold change (log2FC) of RAB8B across various tissues in bulk RNA-seq data obtained from severe COVID-19 cases.
            </p>
        </section>

        {/* ACORDEÃO DE DADOS */}
        <section className="accordion-section">
            <StudiesAccordion data={studiesData} />
        </section>

      </StyledContainer>
    </>
  );
}

// ESTILOS CSS
const StyledContainer = styled.div`
    width: 85vw; 
    max-width: 1000px;
    margin: 40px auto 80px auto; 
    font-family: sans-serif; 
    display: flex;
    flex-direction: column;
    gap: 40px; 

    /* REGRA GLOBAL DE NEGRITO */
    strong {
        font-weight: bold; 
    }

    /* HEADER */
    .main-header-row {
        display: flex;
        align-items: center; 
        gap: 20px;
        padding-bottom: 20px;
        border-bottom: 2px solid #ccc;
    }

    .main-title-text {
        margin: 0;
        font-size: 1.8em; 
        color: #333;
        font-weight: 500;
    }
    
    .logo { height: 80px; width: auto; }
    
    /* GERAL */
    .content-box { padding: 16px; border-radius: 6px; }

   /* TEXTO PRINCIPAL */
    .summary-section {
        border: none;
        background-color: transparent;
        font-size: 1.0em;
        line-height: 1.5;
        padding: 10px 16px; 
        text-align: justify;
        hyphens: auto;

        ul {
            list-style-type: none; /* Removemos as bolinhas padrão para focar nos botões */
            margin-left: 10px; 
            padding-left: 0;
            margin-top: 15px;
            margin-bottom: 15px;
        }
        
        li {
            margin-bottom: 15px; /* Mais espaço entre os itens */
            display: block;
        }
        
        /* ESTILO DE LINK APRIMORADO (BOTÃO) */
        a {
            text-decoration: none;
            color: #0d3c61; /* Texto azul escuro */
            background-color: #AFD1FF; 
            padding: 6px 12px;
            border-radius: 20px; /* Bordas mais arredondadas (pílula) parecem mais clicáveis */
            font-weight: bold; 
            display: inline-flex; /* Melhora alinhamento com ícones */
            align-items: center;
            
            border: 1px solid #9dc4f8;
            box-shadow: 0 2px 5px rgba(0,0,0,0.08);
            transition: all 0.2s ease;
        }

        a:hover {
            background-color: #8cbcfc;
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.15);
        }

        /* ÍCONE DE SETA AUTOMÁTICO */
        a::after {
            content: "➜";
            font-size: 0.9em;
            margin-left: 8px;
            transition: transform 0.2s;
        }

        a:hover::after {
            transform: translateX(4px);
        }
    }
    
    /* PAINEL DE DESTAQUE */
    .transition-text {
        text-align: center;
        padding: 20px 40px;
        margin: 20px auto;
        max-width: 800px;
        font-size: 1.05em;
        line-height: 1.6;
        background-color: #F2F2F2; 
        border-radius: 8px; 
        p { margin: 0; }
    }

    /* IMAGENS E LEGENDAS */
    .image-box { border: none; padding: 0; margin: 0; background: transparent; }

    .center-container {
        display: flex;
        flex-direction: column; 
        justify-content: center; 
        align-items: center; 
        width: 100%;
        margin-top: 60px;
    }

    .section-image { width: 100%; height: auto; max-width: 80%; display: block; margin: 0 auto; }
    .reduced-width { max-width: 40% !important; }

    .figure-title {
        font-size: 1.25em;
        font-weight: bold;
        color: #222;
        margin-bottom: 20px;
        text-align: center;
        width: 100%;
    }

    .figure-caption {
        margin-top: 20px;      
        font-size: 0.95rem;     
        color: #444;           
        line-height: 1.5;
        text-align: justify;   
        width: 80%; 
    }

    /* ESTILOS DO ACORDEÃO */
    .accordion-section {
        margin-top: 40px;
        border-top: 1px solid #eee;
        padding-top: 20px;
    }

    .accordion-header {
        background-color: #f9f9f9;
        color: #444;
        cursor: pointer;
        padding: 15px;
        width: 100%;
        border: 1px solid #ddd;
        border-radius: 5px;
        text-align: left;
        outline: none;
        font-size: 1rem;
        font-weight: 600;
        display: flex;
        justify-content: space-between;
        align-items: center;
        transition: background-color 0.2s ease;
    }

    .accordion-header:hover { background-color: #f1f1f1; }

    .arrow { transition: transform 0.2s ease; font-size: 0.8rem; }
    .arrow.open { transform: rotate(180deg); }

    .accordion-content { padding: 10px 0; overflow-x: auto; }

    .studies-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.85rem;
        margin-top: 10px;
    }

    .studies-table th, .studies-table td {
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
    }

    .studies-table th { background-color: #f2f2f2; color: #333; font-weight: bold; }
    .studies-table tr:nth-child(even) { background-color: #fbfbfb; }
    .studies-table tr:hover { background-color: #f1f1f1; }
    
    .studies-table a { color: #0066cc; text-decoration: none; }
    .studies-table a:hover { text-decoration: underline; }

    @media (max-width:600px){
        width: 95%;
        margin: 20px auto;
        
        .main-header-row { flex-direction: column; text-align: center; }
        .figure-caption { text-align: left; width: 100%; }
        .section-image { max-width: 100%; }
        .reduced-width { max-width: 80% !important; }
        .transition-text { padding: 15px; }
    }
`